import { useSelector } from "src/store";
import { useLicenses } from "src/ui/containers/dashboard/licenses/api/helpers";

import { defineAbility, ForcedSubject, MongoAbility, subject } from "@casl/ability";
import { Device, Group, GroupMember, License } from "@dashboard/devices/types";
import { FileInfo } from "@dashboard/files/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Workspace } from "@dashboard/workspaces/types";

import AuthService from "../authService";

type GroupAbilities = ["create" | "delete" | "update", "Group"];
type LicenseAbilities = ["manage" | "create", "License"];
type WorkspaceAbilities = ["update" | "cancel", "Workspace"];
type FileAbilities = ["manage", "File"];
type AppAbilities = ["install" | "uninstall", "App"];
type TagAbilities = ["manage", "Tag"];
type MemberSubject = ForcedSubject<"Member"> & { role: GroupMember["role"] | undefined };
type MemberAbilities = ["manage" | "create" | "update", "Member" | MemberSubject];
type UniversalLicenseAbilities = ["manage", "UniversalLicense"];
type ProductAbilities = ["manage" | "delete", "Product"];

type Abilities =
    | GroupAbilities
    | LicenseAbilities
    | WorkspaceAbilities
    | FileAbilities
    | AppAbilities
    | TagAbilities
    | MemberAbilities
    | UniversalLicenseAbilities
    | ProductAbilities;

export const memberSubject = (role: GroupMember["role"] | undefined): MemberSubject => subject("Member", { role });

const getGroupRole = (email: string | undefined, group: Group | null, groups: Group[]): GroupMember["role"] | null => {
    if (!email || !group) {
        return null;
    }

    const currentGroupRole = group.members.find((member) => member.email === email)?.role || null;

    if (!group.parentId) {
        return currentGroupRole;
    }

    const parentGroup = groups.find((g) => g.id === group.parentId);
    if (!parentGroup) {
        return currentGroupRole;
    }

    const parentGroupRole = getGroupRole(email, parentGroup, groups);

    if (currentGroupRole === "Owner" || parentGroupRole === "Owner") {
        return "Owner";
    }
    if (currentGroupRole === "Manager" || parentGroupRole === "Manager") {
        return "Manager";
    }
    if (currentGroupRole === "User" || parentGroupRole === "User") {
        return "User";
    }

    return null;
};

const getEffectiveRole = (group: Group | null, workspace: Workspace | null, email: string | undefined, groups: Group[]) => {
    const workspaceRole = workspace?.members.find((member) => member.email === email)?.role || null;
    const groupRole = getGroupRole(email, group, groups);

    if (groupRole === "Owner" || workspaceRole === "Owner") {
        return "Owner";
    }
    if (groupRole === "Manager" || workspaceRole === "Manager") {
        return "Manager";
    }
    if (groupRole === "User" || workspaceRole === "User") {
        return "User";
    }
    return null;
};

type AbilityCannot = Parameters<Parameters<typeof defineAbility<MongoAbility<Abilities>>>[0]>[1];

/**
 * Wind-down rule for members: the workspace keeps its own member list readable and its members
 * removable, but it may no longer grow or be reorganized — no invitations and no role changes.
 *
 * Call this as the very last rule of an ability: CASL lets the last matching rule win, so an Owner's
 * unconditional `can("manage", "Member")` would otherwise grant the revoked actions back.
 */
const revokeMemberChangesOnExpiry = (cannot: AbilityCannot, subscriptionExpired: boolean) => {
    if (!subscriptionExpired) {
        return;
    }
    cannot(["create", "update"], "Member");
};

export const PermissionService = (email?: string) => {
    const groups = useSelector(selectGroups);
    const subscriptionExpired = useCurrentSubscriptionExpired();

    const { data: currentWorkspace } = useCurrentWorkspace();

    const emailAdrress = email || AuthService.getCurrentUser()?.email || "test@emteria.com";

    return {
        workspaceAbility: (workspace: Workspace | null) => {
            const role = getEffectiveRole(null, workspace, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, cannot) => {
                if (subscriptionExpired) {
                    if (role === "Owner") {
                        can(["cancel"], "Workspace");
                    }
                    if (role === "Owner" || role === "Manager") {
                        can(["manage"], "Member", { role: { $ne: "Owner" } });
                    }
                } else {
                    if (role === "Owner") {
                        can(["cancel"], "Workspace");
                    }
                    if (role === "Owner" || role === "Manager") {
                        can(["update"], "Workspace");
                        can(["manage"], "Member", { role: { $ne: "Owner" } });
                    }
                    if (role === "Owner" || role === "Manager" || role === "User") {
                        can(["manage"], "File");
                        can(["create"], "License");
                    }
                }

                if (role === "Owner") {
                    can(["manage"], "Member");
                }

                revokeMemberChangesOnExpiry(cannot, subscriptionExpired);
            });
        },
        groupAbility: (group: Group | null, workspace: Workspace | null) => {
            const role = getEffectiveRole(group, workspace, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, cannot) => {
                if (subscriptionExpired) {
                    if (role === "Owner" || role === "Manager") {
                        can(["manage"], "Member", { role: { $ne: "Owner" } });
                    }
                } else if (role === "Owner" || role === "Manager") {
                    can(["create", "delete", "update"], "Group");
                    can(["manage"], "Member", { role: { $ne: "Owner" } });
                    can(["manage"], "UniversalLicense");
                    can(["install", "uninstall"], "App");
                }
                if (role === "Owner" || role === "Manager" || role === "User") {
                    can(["manage"], "File");
                }

                if (role === "Owner") {
                    can(["manage"], "Member");
                }

                revokeMemberChangesOnExpiry(cannot, subscriptionExpired);
            });
        },
        licenseAbility: (license: License | null, workspace?: Workspace) => {
            const group = groups.find((item) => item.id === license?.groupId);
            const role = getEffectiveRole(group || null, workspace || currentWorkspace, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, _cannot) => {
                if (subscriptionExpired) {
                    return;
                }
                if (role === "Owner") {
                    can(["manage"], "License");
                }
            });
        },
        fileAbility: (file: FileInfo) => {
            const group = groups.find((item) => item.id === file.groupId);
            const role = getEffectiveRole(group || null, currentWorkspace || null, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, _cannot) => {
                if (subscriptionExpired) {
                    return;
                }
                if (role === "Owner" || role === "Manager") {
                    can(["manage"], "Tag");
                }
            });
        },
        deviceAbility: (device: Device) => {
            const licenses = useLicenses();
            const license = licenses.find((item) => item?.device?.id === device.id);
            const group = groups.find((item) => item.id === license?.groupId);
            const role = getEffectiveRole(group || null, currentWorkspace || null, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, _cannot) => {
                if (subscriptionExpired) {
                    return;
                }
                if (role === "Owner" || role === "Manager") {
                    can(["install", "uninstall"], "App");
                }
            });
        },
        productAbility: (workspace: Workspace | null) => {
            const role = getEffectiveRole(null, workspace, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, _cannot) => {
                if (subscriptionExpired) {
                    if (role === "Owner" || role === "Manager") {
                        can(["delete"], "Product");
                    }
                    return;
                }
                if (role === "Owner" || role === "Manager") {
                    // CASL treats "manage" as the any-action wildcard, so this also covers "delete".
                    can(["manage"], "Product");
                }
            });
        },
    };
};
